import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/apiConfig';
import { CASHFREE_CONFIG } from '../../../config/paymentConfig';

// Keep dummy images as fallbacks
import graphics_card from '../../../assets/profile/graphics_card.jpg';
import iphone_16 from '../../../assets/profile/iphone-16-pro.webp';
import jbl from '../../../assets/profile/jbl.jpg';
import ultraviolit_bike from '../../../assets/profile/ultraviolit_bike.png';
import asus_rog_zephyrus from '../../../assets/profile/asus.jpg';

// Helper function to render star ratings
function renderStars(rating) {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

// Helper function to turn URLs into clickable links that open in a new tab
function renderWithLinks(text) {
  if (typeof text !== 'string') return text;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return (
        <a 
          key={i} 
          href={part} 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ 
            display: 'inline-block',
            backgroundColor: '#e9fe53',
            color: '#202023',
            padding: '8px 24px',
            textDecoration: 'none',
            fontWeight: '800',
            border: '1px solid #202023',
            fontFamily: 'Metrophobic, sans-serif',
            fontSize: '14px',
            marginTop: '5px',
            marginBottom: '5px'
          }}
        >
          Pre-book now
        </a>
      );
    }
    return part;
  });
}

// Fallback images array for products without images
const fallbackImages = [graphics_card, iphone_16, jbl, ultraviolit_bike, asus_rog_zephyrus];

// Styled components for variant selection (must be defined outside the component)
const VariantSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 4px 0;
`;

const VariantChip = styled.button`
  border: 1px solid #202023;
  background: ${props => (props.$selected ? '#e9fe53' : '#fff')};
  color: #202023;
  padding: 6px 10px;
  border-radius: 0;
  font-size: 12px;
  cursor: pointer;
`;

function Dealsboard({ isSubscriptionAllowed, onSubscribe, navigateToLists }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
    const [variants, setVariants] = useState([]);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [deliveryDetails, setDeliveryDetails] = useState({
      name: '',
      email: '',
      phone: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      paymentMethod: 'cod',
      gst: '',
      companyName: ''
    });
    const [billToCompany, setBillToCompany] = useState(false);
    const [nowTs, setNowTs] = useState(Date.now());
    const [serviceability, setServiceability] = useState({ status: null, message: '' });
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [profileLoaded, setProfileLoaded] = useState(false);

    const maskPrice = (price) => {
      if (!price) return '';
      const s = Math.floor(parseFloat(price)).toString();
      if (s.length <= 1) return '₹' + s;
      if (s.length === 2) return '₹' + s[0] + s[1];
      return '₹' + s[0] + '*'.repeat(s.length - 2) + s[s.length - 1];
    };

    // Fetch products from API
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/product/list?enabled_only=true&limit=15`, {
          headers: token ? { Authorization: token } : {}
        });
        
        if (response.data.is_success) {
          // Transform API products to match expected format
          const transformedProducts = response.data.data.products.map((product, index) => ({
            id: product.id,
            name: product.name,
            image: product.images && product.images.length > 0 ? product.images[0] : fallbackImages[index % fallbackImages.length],
            images: product.images && product.images.length > 0 ? product.images : [fallbackImages[index % fallbackImages.length]],
            progress: Math.floor(Math.random() * 40) + 30,
            daysRemaining: Math.floor(Math.random() * 15) + 1,
            moreNeeded: Math.floor(Math.random() * 100) + 50,
            rating: Math.floor(Math.random() * 2) + 4,
            currentPrice: product.price,
            originalPrice: product.amazonPrice || product.price,
            category: product.category,
            brand: product.brand,
            stock: product.stock,
            descriptions: product.descriptions || [],
            sku: product.sku,
            variantGroup: product.variantGroup || null,
            isVariantPrimary: !!product.isVariantPrimary,
            saleStartAt: product.saleStartAt || null,
            saleEndAt: product.saleEndAt || null
          }));

          // Collapse variants: if products share a variantGroup, show only the primary
          // Fallback: if a group has no primary, keep the first encountered item
          const groupHasPrimary = new Map();
          for (const p of transformedProducts) {
            if (p.variantGroup) {
              const key = p.variantGroup;
              const current = groupHasPrimary.get(key) || false;
              groupHasPrimary.set(key, current || p.isVariantPrimary === true);
            }
          }

          const firstKeptForGroup = new Set();
          const filteredProducts = transformedProducts.filter((p) => {
            if (!p.variantGroup) return true;
            const key = p.variantGroup;
            const hasPrimary = groupHasPrimary.get(key) === true;
            if (hasPrimary) {
              return p.isVariantPrimary === true;
            }
            // No primary in this group: keep only first seen item
            if (!firstKeptForGroup.has(key)) {
              firstKeptForGroup.add(key);
              return true;
            }
            return false;
          });

          // After filtering, prefer primary variant name for grouped products
          const productsWithVariantNames = await Promise.all(filteredProducts.map(async (p) => {
            if (!p.variantGroup) return p;
            try {
              const resp = await axios.get(`${API_BASE_URL}/product/${p.id}/variants`);
              const arr = resp?.data?.data?.variants;
              if (Array.isArray(arr) && arr.length > 0) {
                const primary = arr.find(v => v.isVariantPrimary) || arr[0];
                if (primary?.name && primary.name !== p.name) {
                  return { ...p, name: primary.name };
                }
              }
            } catch (e) {}
            return p;
          }));

          setProducts(productsWithVariantNames);
        } else {
          setError('Failed to fetch products');
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchProducts();
    }, []);

    // Tick every second for countdowns
    useEffect(() => {
      const timer = setInterval(() => setNowTs(Date.now()), 1000);
      return () => clearInterval(timer);
    }, []);

    useEffect(() => {
      setActiveImageIndex(0);
    }, [selectedProduct?.id]);

    const openModal = async (product) => {
      setSelectedProduct(product);
      setModalOpen(true);
      setActiveImageIndex(0);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling

      // Fetch variants for this product (if any)
      try {
        const resp = await axios.get(`${API_BASE_URL}/product/${product.id}/variants`);
        if (resp.data && resp.data.is_success) {
          const fetched = resp.data.data.variants || [];
          // Transform to UI shape used here
          const transformed = fetched.map((p, index) => ({
            id: p.id,
            name: p.name,
            image: p.images && p.images.length > 0 ? p.images[0] : (product.image || ''),
            images: Array.isArray(p.images) && p.images.length > 0 ? p.images : (product.images || []),
            currentPrice: p.price,
            originalPrice: p.amazonPrice || p.price,
            category: p.category,
            brand: p.brand,
            stock: p.stock,
            descriptions: p.descriptions || [],
            sku: p.sku,
            isPrimary: !!p.isVariantPrimary,
            saleStartAt: p.saleStartAt || null,
            saleEndAt: p.saleEndAt || null
          }));
          // If no group, API returns single self product — that's fine
          setVariants(transformed);
          // Prefer primary variant as selected if current product isn't already it
          const primary = transformed.find(v => v.isPrimary);
          if (primary && primary.id !== product.id) {
            setSelectedProduct(primary);
          }
        } else {
          setVariants([]);
        }
      } catch (e) {
        console.error('Failed to fetch variants', e);
        setVariants([]);
      }
    };

    const closeModal = () => {
      setModalOpen(false);
      setSelectedProduct(null);
      setVariants([]);
      document.body.style.overflow = 'unset'; // Restore scrolling
    };

    // Fetch user profile data
    const fetchUserProfile = async () => {
      try {
        setLoadingProfile(true);
        const token = sessionStorage.getItem('token');
        if (!token) {
          console.log('No token found, user not logged in');
          setProfileLoaded(false);
          return;
        }

        const response = await axios.post(`${API_BASE_URL}/user/get-profile`, {}, {
          headers: {
            'Authorization': token
          }
        });

        if (response.data.is_success && response.data.data && response.data.data.length > 0) {
          const userProfile = response.data.data[0];
          console.log(userProfile);
          // Pre-populate delivery details with user profile data
          setDeliveryDetails(prev => ({
            ...prev,
            name: userProfile.name || userProfile.firstname + ' ' + userProfile.lastname || '',
            email: userProfile.email || '',
            phone: userProfile.phone || userProfile.phoneNumber || userProfile.mobile || '',
            address: userProfile.address || userProfile.fullAddress || '',
            pincode: userProfile.pincode || userProfile.zipCode || userProfile.postalCode || '',
            city: userProfile.city || '',
            state: userProfile.state || '',
            gst: userProfile.gst || '',
            companyName: userProfile.companyName || ''
          }));
          // Reset billToCompany when profile loads
          setBillToCompany(false);
          
          console.log('Profile data loaded successfully:', userProfile);
          setProfileLoaded(true);
          
          // Auto-hide success message after 3 seconds
          setTimeout(() => {
            setProfileLoaded(false);
          }, 3000);
        } else {
          console.log('No profile data found or API response unsuccessful');
          setProfileLoaded(false);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setProfileLoaded(false);
        // Continue with empty form if profile fetch fails
      } finally {
        setLoadingProfile(false);
      }
    };

    const handleBuyNow = async (product) => {
      // Close product modal and open delivery modal
      setModalOpen(false);
      setDeliveryModalOpen(true);
      
      // Fetch and populate user profile data
      await fetchUserProfile();
    };

    const closeDeliveryModal = () => {
      setDeliveryModalOpen(false);
      setLoadingProfile(false);
      setProfileLoaded(false);
      setBillToCompany(false);
      setDeliveryDetails({
        name: '',
        email: '',
        phone: '',
        address: '',
        pincode: '',
        city: '',
        state: '',
        paymentMethod: 'cod',
        gst: '',
        companyName: ''
      });
      document.body.style.overflow = 'unset';
    };

    const handleDeliveryDetailsChange = (e) => {
      const { name, value } = e.target;
      setDeliveryDetails(prev => ({
        ...prev,
        [name]: value
      }));
    };

    // Auto fill city/state and validate serviceability on pincode entry
    useEffect(() => {
      const pin = deliveryDetails.pincode;
      if (!pin || String(pin).length !== 6) {
        setServiceability({ status: null, message: '' });
        return;
      }
      let cancelled = false;
      const check = async () => {
        try {
          const resp = await axios.get(`${API_BASE_URL}/shipping/bluedart/pincode/${pin}`);
          const data = resp.data;
          if (cancelled) return;
          if (data?.is_success) {
            const d = data.data || {};
            setDeliveryDetails(prev => ({
              ...prev,
              city: d.pincodeDescription || d.cityDescription || prev.city,
              state: (d.state || prev.state).toLowerCase().replace(/^\w/, c => c.toUpperCase())
            }));
            const ok = !!d.serviceable;
            setServiceability({ status: ok, message: ok ? '' : 'Not serviceable currently' });
          } else {
            setServiceability({ status: false, message: 'Not serviceable currently' });
          }
        } catch (e) {
          if (!cancelled) setServiceability({ status: false, message: 'Not serviceable currently' });
        }
      };
      check();
      return () => { cancelled = true; };
    }, [deliveryDetails.pincode]);

    const handleConfirmOrder = async () => {
      // Validate delivery details
      const { name, phone, address, pincode, city, state } = deliveryDetails;
      if (!name || !phone || !address || !pincode || !city || !state) {
        alert('Please fill in all delivery details');
        return;
      }

      // Validate company billing fields
      if (billToCompany) {
        if (!deliveryDetails.companyName) {
          alert('Please enter company name for company billing');
          return;
        }
        if (!deliveryDetails.gst) {
          alert('Please enter GSTIN for company billing');
          return;
        }
      }

      try {
        setLoadingProfile(true);
        
        const token = sessionStorage.getItem('token');
        if (!token) {
          alert('Please login to continue with your order');
          return;
        }

        // Update user profile with company name and GST if billing to company
        if (billToCompany && (deliveryDetails.companyName || deliveryDetails.gst)) {
          try {
            await axios.post(`${API_BASE_URL}/user/update-profile`, {
              companyName: deliveryDetails.companyName,
              gst: deliveryDetails.gst
            }, {
              headers: {
                'Authorization': token,
                'Content-Type': 'application/json'
              }
            });
            console.log('User profile updated with company name and GST');
          } catch (profileError) {
            console.error('Error updating user profile with company info:', profileError);
            // Continue with order even if profile update fails
          }
        }

        // Prepare billing address with company details if applicable
        const billingAddress = {
          name: billToCompany ? deliveryDetails.companyName : deliveryDetails.name,
          phone: deliveryDetails.phone,
          address: deliveryDetails.address,
          city: deliveryDetails.city,
          state: deliveryDetails.state,
          pincode: deliveryDetails.pincode,
          // Include company billing fields
          bill_to_company: billToCompany,
          company_name: billToCompany ? deliveryDetails.companyName : null,
          gst: billToCompany ? deliveryDetails.gst : null
        };

        // Prepare order data
        const orderData = {
          amount: selectedProduct.currentPrice,
          customer_email: deliveryDetails.email,
          customer_phone: deliveryDetails.phone,
          first_name: deliveryDetails.name.split(' ')[0] || deliveryDetails.name,
          last_name: deliveryDetails.name.split(' ').slice(1).join(' ') || '',
          product_id: selectedProduct.id,
          quantity: 1,
          description: `Purchase of ${selectedProduct.name}`,
          return_url: `${window.location.origin}/order`,
          shipping_address: {
            name: deliveryDetails.name,
            phone: deliveryDetails.phone,
            address: deliveryDetails.address,
            city: deliveryDetails.city,
            state: deliveryDetails.state,
            pincode: deliveryDetails.pincode
          },
          billing_address: billingAddress
        };

        console.log('Creating order with data:', orderData);

        const response = await axios.post(`${API_BASE_URL}/product/order`, orderData, {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          }
        });

        console.log('Order creation response:', response.data);
        console.log('response.data.is_success:', response.data.is_success);
        console.log('response.data.data:', response.data.data);
        if (response.data.is_success) {
          const orderResult = response.data.data;
          
          // Store order ID in session storage for tracking
          sessionStorage.setItem('current_order_id', orderResult.order_id);
          sessionStorage.setItem('order_type', 'product');
          
          // Use Cashfree SDK if payment_session_id present
          const paymentSessionId = orderResult?.payment_session_id;
          const paymentLink = orderResult?.payment_link || orderResult?.cashfree_response?.payment_link || orderResult?.cashfree_response?.payment_links?.web;

          if (paymentSessionId) {
            try {
              const { load: loadCashfree } = await import('@cashfreepayments/cashfree-js');
              const cashfree = await loadCashfree({ mode: CASHFREE_CONFIG.mode });
              await cashfree.checkout({
                paymentSessionId,
                redirectTarget: '_self'
              });
            } catch (e) {
              console.error('Cashfree checkout error, falling back to link:', e);
              if (paymentLink) window.location.href = paymentLink;
            }
          } else if (paymentLink) {
            window.location.href = paymentLink;
          } else {
            // Fallback: redirect to order status page
            console.log('No payment URL found, redirecting to order status');
            window.location.href = `/order?order_id=${orderResult.order_id}`;
          }
        } else {
          console.error('Order creation failed:', response.data);
          alert(`Order creation failed: ${response.data.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error creating order:', error);
        
        if (error.response && error.response.data) {
          alert(`Order creation failed: ${error.response.data.message || error.response.data.error || 'Unknown error'}`);
        } else {
          alert('Failed to create order. Please try again.');
        }
      } finally {
        setLoadingProfile(false);
        closeDeliveryModal();
        setSelectedProduct(null);
      }
    };

    const calculateSavings = (currentPrice, originalPrice) => {
      if (!originalPrice || currentPrice >= originalPrice) return null;
      const savings = ((originalPrice - currentPrice) / originalPrice * 100).toFixed(0);
      return savings;
    };

    if (loading) {
      return (
        <DealsSection>
          <DealsContent>
            <LoadingText>Loading amazing deals...</LoadingText>
          </DealsContent>
        </DealsSection>
      );
    }

    if (error) {
      return (
        <DealsSection>
          <DealsContent>
            <ErrorText>{error}</ErrorText>
            <RetryButton onClick={fetchProducts}>Try Again</RetryButton>
          </DealsContent>
        </DealsSection>
      );
    }

    return (
      <>
        <DealsSection>
          <DealsContent>
            {products.length > 0 ? (
              <>
                {/* Feature Deal of the Day Banner */}
                {(() => {
                  const preBookingProducts = products.filter(p => {
                    const start = p.saleStartAt ? new Date(p.saleStartAt).getTime() : null;
                    return start && nowTs < start;
                  });

                  const bannerProducts = preBookingProducts.length > 0 ? preBookingProducts : (products.length > 0 ? [products[0]] : []);
                  
                  const renderBannerItem = (featured) => {
                    if (!featured) return null;
                    const startTarget = featured.saleStartAt ? new Date(featured.saleStartAt).getTime() : nowTs + (3 * 24 * 60 * 60 * 1000);
                    const msTo = (target) => Math.max(0, target - nowTs);
                    const fmtDhms = (ms) => {
                      const totalSec = Math.floor(ms / 1000);
                      const days = Math.floor(totalSec / 86400);
                      const hours = Math.floor((totalSec % 86400) / 3600);
                      const mins = Math.floor((totalSec % 3600) / 60);
                      const secs = totalSec % 60;
                      const totalHours = hours + (days * 24);
                      return { days, hours, mins, secs, totalHours };
                    };
                    const timer = fmtDhms(msTo(startTarget));
                    const isCounterActive = msTo(startTarget) > 0;

                    return (
                      <FeaturedBanner 
                        key={featured.id} 
                        onClick={() => openModal(featured)} 
                        style={{ 
                          marginTop: preBookingProducts.length > 1 ? 0 : '30px', 
                          height: '100%',
                          // Force edge-to-edge for single banner if not in carousel
                          ...(preBookingProducts.length <= 1 && window.innerWidth <= 991 ? {
                            width: 'calc(100% + 40px)',
                            marginLeft: '-20px',
                            marginRight: '-20px'
                          } : {})
                        }}
                      >
                        <FeaturedInfo>
                          <Badge>DEAL OF THE DAY</Badge>
                          <FeaturedTitle>{featured.name}</FeaturedTitle>
                          <SavingsBannerText>
                            {featured.originalPrice && (
                              `Upto ₹${Math.ceil((parseFloat(featured.originalPrice) - parseFloat(featured.currentPrice)) / 1000) * 1000} off`
                            )}
                          </SavingsBannerText>

                          <TimerContainer>
                            <TimerLabel>Hurry. Sales start in</TimerLabel>
                            <TimerBoxes>
                              {timer.totalHours > 72 && (
                                <TimerBox>
                                  <TimerDigit>{String(timer.days).padStart(2, '0')}</TimerDigit>
                                  <TimerUnit>DAYS</TimerUnit>
                                </TimerBox>
                              )}
                              <TimerBox>
                                <TimerDigit>{String(timer.totalHours > 72 ? timer.hours : timer.totalHours).padStart(2, '0')}</TimerDigit>
                                <TimerUnit>HRS</TimerUnit>
                              </TimerBox>
                              <TimerBox>
                                <TimerDigit>{String(timer.mins).padStart(2, '0')}</TimerDigit>
                                <TimerUnit>MIN</TimerUnit>
                              </TimerBox>
                              <TimerBox>
                                <TimerDigit>{String(timer.secs).padStart(2, '0')}</TimerDigit>
                                <TimerUnit>SEC</TimerUnit>
                              </TimerBox>
                            </TimerBoxes>
                          </TimerContainer>

                          <BannerActionGroup>
                            <BuyNowBannerButton onClick={(e) => {
                              e.stopPropagation();
                              if (!isSubscriptionAllowed) {
                                onSubscribe();
                              } else {
                                openModal(featured);
                              }
                            }}>
                              {!isSubscriptionAllowed ? 'SUBSCRIBE TO PRE-BOOK' : (isCounterActive ? 'PRE-BOOK NOW' : 'BUY NOW')}
                            </BuyNowBannerButton>
                            <SecondaryBannerButton onClick={(e) => {
                              e.stopPropagation();
                              openModal(featured);
                            }}>
                              VIEW DETAILS
                            </SecondaryBannerButton>
                          </BannerActionGroup>
                        </FeaturedInfo>
                        <FeaturedImageContainer>
                          <FeaturedImage src={featured.image} alt={featured.name} />
                        </FeaturedImageContainer>
                      </FeaturedBanner>
                    );
                  };

                  if (bannerProducts.length > 1) {
                    return (
                      <CarouselWrapper>
                        <Carousel
                          responsive={{
                            desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
                            tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
                            mobile: { breakpoint: { max: 464, min: 0 }, items: 1 }
                          }}
                          infinite={true}
                          autoPlay={true}
                          autoPlaySpeed={5000}
                          keyBoardControl={true}
                          customTransition="transform 600ms cubic-bezier(0.4, 0, 0.2, 1)"
                          transitionDuration={600}
                          containerClass="carousel-container"
                          itemClass="carousel-item-equal-height"
                          removeArrowOnDeviceType={["tablet", "mobile"]}
                          dotListClass="custom-dot-list-style"
                          showDots={true}
                        >
                          {bannerProducts.map(featured => renderBannerItem(featured))}
                        </Carousel>
                        <style>{`
                          .carousel-container { overflow: hidden; width: 100%; position: relative; }
                          .react-multi-carousel-track { display: flex; }
                          .carousel-item-equal-height { display: flex; align-items: stretch; height: auto; width: 100%; }
                          .react-multi-carousel-dot-list { margin-bottom: 30px; }
                          .react-multi-carousel-dot button { border: 1px solid rgba(233, 254, 83, 0.4); background: transparent; width: 8px; height: 8px; }
                          .react-multi-carousel-dot--active button { background: #e9fe53; border-color: #e9fe53; opacity: 1; }
                          
                          /* Show arrows only on hover for desktop */
                          @media (min-width: 1024px) {
                            .react-multiple-carousel__arrow { opacity: 0; transition: opacity 0.3s ease-in-out; visibility: hidden; }
                            .carousel-container:hover .react-multiple-carousel__arrow { opacity: 1; visibility: visible; }
                          }
                        `}</style>
                      </CarouselWrapper>
                    );
                  } else if (bannerProducts.length === 1) {
                    return renderBannerItem(bannerProducts[0]);
                  }
                  return null;
                })()}

                {/* Toolbar Section */}
                <Toolbar>
                  <ProductCount>Showing {products.length} products</ProductCount>
                  <ToolbarActions>
                    <ToolbarSelect defaultValue="default">
                      <option value="default">Sort by</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="newest">Newest First</option>
                    </ToolbarSelect>
                    <ToolbarSelect defaultValue="default">
                      <option value="default">Category: All</option>
                      {Array.from(new Set(products.map(p => p.category))).filter(Boolean).map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </ToolbarSelect>
                  </ToolbarActions>
                </Toolbar>

                <ProductGrid>
                  {products.slice(0, 15).map((product) => {
                    const savings = calculateSavings(parseFloat(product.currentPrice), parseFloat(product.originalPrice));
                    const start = product.saleStartAt ? new Date(product.saleStartAt).getTime() : null;
                    const end = product.saleEndAt ? new Date(product.saleEndAt).getTime() : null;
                    const isBefore = start && nowTs < start;
                    const isAfter = end && nowTs > end;
                    const isActive = (!start || nowTs >= start) && (!end || nowTs <= end);

                    const msTo = (target) => Math.max(0, target - nowTs);
                    const fmtDhms = (ms) => {
                      const totalSec = Math.floor(ms / 1000);
                      const days = Math.floor(totalSec / 86400);
                      const hours = Math.floor((totalSec % 86400) / 3600);
                      const mins = Math.floor((totalSec % 3600) / 60);
                      const secs = totalSec % 60;
                      return { days, hours, mins, secs };
                    };

                    const beforeMsg = isBefore && start ? (() => {
                      const t = fmtDhms(msTo(start));
                      return `Sale starts in ${t.days}d ${t.hours}h ${t.mins}m`;
                    })() : null;

                    const activeMsg = isActive && end ? (() => {
                      const t = fmtDhms(msTo(end));
                      return `Sale ends in ${t.days}d ${t.hours}h ${t.mins}m ${t.secs}s`;
                    })() : null;

                    return (
                      <ProductCard key={product.id} onClick={() => openModal(product)}>
                        <ProductImageContainer>
                          <ProductImage src={product.image} alt={product.name} />
                          <ImageOverlay>
                            <ViewDetailsText>View Details</ViewDetailsText>
                          </ImageOverlay>
                        </ProductImageContainer>
                        <ProductInfo>
                          <div style={{display: 'flex', height: '1px', width: '100%', backgroundColor: '#e0e0e0'}}></div>
                          <ProductTitle title={(product.variantGroup && product.variantGroup.trim() !== '' ? product.variantGroup : product.name)}>
                            {(product.variantGroup && product.variantGroup.trim() !== '' ? product.variantGroup : product.name).length > 60 
                              ? `${(product.variantGroup && product.variantGroup.trim() !== '' ? product.variantGroup : product.name).substring(0, 60)}...` 
                              : (product.variantGroup && product.variantGroup.trim() !== '' ? product.variantGroup : product.name)}
                          </ProductTitle>
                          <PriceContainer>
                            {!isSubscriptionAllowed ? (
                              <>
                                <CurrentPrice $isMasked>
                                  {maskPrice(product.currentPrice)}
                                </CurrentPrice>
                                {product.originalPrice && parseFloat(product.originalPrice) !== parseFloat(product.currentPrice) && (
                                  <OriginalPrice>₹{parseFloat(product.originalPrice).toLocaleString('en-IN')}</OriginalPrice>
                                )}
                              </>
                            ) : (
                              <>
                                <CurrentPrice>₹{parseFloat(product.currentPrice).toLocaleString('en-IN')}</CurrentPrice>
                                {product.originalPrice && parseFloat(product.originalPrice) !== parseFloat(product.currentPrice) && (
                                  <OriginalPrice>₹{parseFloat(product.originalPrice).toLocaleString('en-IN')}</OriginalPrice>
                                )}
                              </>
                            )}
                          </PriceContainer>
                          {beforeMsg && (
                            <div style={{ color: '#dc3545', fontSize: 12, marginTop: 6, textAlign: 'center' }}>{beforeMsg}</div>
                          )}
                          {activeMsg && (
                            <div style={{ color: '#16a34a', fontSize: 12, marginTop: 6, textAlign: 'center' }}>{activeMsg}</div>
                          )}
                          <CardFooter>
                            {product.stock <= 5 && product.stock > 0 && (
                              <StockText>Only {product.stock} left!</StockText>
                            )}
                          </CardFooter>
                          <ViewDetailsButton 
                            onClick={(e) => {
                              if (!isSubscriptionAllowed) {
                                e.stopPropagation();
                                onSubscribe();
                              } else {
                                openModal(product);
                              }
                            }} 
                            disabled={isSubscriptionAllowed && isAfter} 
                            style={{ display: 'block', margin: 'auto auto 0' }}
                          >
                            {!isSubscriptionAllowed ? 'Subscribe' : (isAfter ? 'Sale Ended' : (isBefore ? 'Pre-book Now' : 'Buy Now'))}
                          </ViewDetailsButton>
                        </ProductInfo>
                      </ProductCard>
                    );
                  })}
                </ProductGrid>
              </>
            ) : (
                <LimitedMerchandise>
                  No products available at the moment.<br />
                  Please check back later for amazing deals!
                </LimitedMerchandise>
            )}

            <PhilosophySection>
              <PhilosophyContent>
                <PhilosophyBadge>[OUR PHILOSOPHY]</PhilosophyBadge>
                <PhilosophyTitle>
                  Unlike other e-commerce platforms, we believe less is more. Hence we showcase only 15 items or less”
                </PhilosophyTitle>
                
                <PhilosophyStats>
                  <StatItem>
                    <StatNumber>5</StatNumber>
                    <StatLabel>FROM YOUR LISTS</StatLabel>
                  </StatItem>
                  
                  <StatPlus>+</StatPlus>
                  
                  <StatItem>
                    <StatNumber>5</StatNumber>
                    <StatLabel>COMMUNITY DEALS</StatLabel>
                  </StatItem>
                  
                  <StatPlus>+</StatPlus>
                  
                  <StatItem>
                    <StatNumber>5</StatNumber>
                    <StatLabel>EXCLUSIVE LAUNCHES</StatLabel>
                  </StatItem>
                </PhilosophyStats>
                
                <PhilosophyFooter>
                  Your products are being grouped with other community members for curating the best deals.<br />
                  For great deals, be sure to complete your <PhilosophyLink onClick={navigateToLists}>Need and Greed lists</PhilosophyLink>
                </PhilosophyFooter>
              </PhilosophyContent>
            </PhilosophySection>
          </DealsContent>
        </DealsSection>

        {/* Product Modal */}
        {modalOpen && selectedProduct && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={closeModal}>×</CloseButton>
              
              <ModalBody>
                <ModalImageSection>
                  <MainImage src={(selectedProduct.images && selectedProduct.images[activeImageIndex]) ? selectedProduct.images[activeImageIndex] : selectedProduct.image} alt={selectedProduct.name} />
                  {selectedProduct.images.length > 1 && (
                    <ImageGallery>
                      {selectedProduct.images.slice(0, 4).map((img, index) => (
                        <ThumbnailImage 
                          key={index} 
                          src={img} 
                          alt={`${selectedProduct.name} ${index + 1}`}
                          onClick={() => setActiveImageIndex(index)}
                          style={{ borderColor: index === activeImageIndex ? '#e9fe53' : 'transparent' }}
                        />
                      ))}
                    </ImageGallery>
                  )}
                </ModalImageSection>

                <ModalInfoSection>
                  <ModalTitle>{selectedProduct.name}</ModalTitle>
                  
                 {/*  <ModalRatingContainer>
                    <RatingStars>{renderStars(selectedProduct.rating)}</RatingStars>
                    <RatingText>({selectedProduct.rating}/5)</RatingText>
                  </ModalRatingContainer> */}

                   <ModalPriceContainer>
                    {!isSubscriptionAllowed ? (
                      <>
                        <ModalCurrentPrice $isMasked>
                          {maskPrice(selectedProduct.currentPrice)}
                        </ModalCurrentPrice>
                        {selectedProduct.originalPrice && parseFloat(selectedProduct.originalPrice) !== parseFloat(selectedProduct.currentPrice) && (
                          <>
                            <ModalOriginalPrice>₹{parseFloat(selectedProduct.originalPrice).toLocaleString('en-IN')}</ModalOriginalPrice>
                            <div style={{ marginLeft: '10px', fontSize: '18px', fontWeight: 'bold', color: '#ff0000' }}>
                              Upto ₹{Math.ceil((parseFloat(selectedProduct.originalPrice) - parseFloat(selectedProduct.currentPrice)) / 1000) * 1000} off
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <ModalCurrentPrice>₹{parseFloat(selectedProduct.currentPrice).toLocaleString('en-IN')}</ModalCurrentPrice>
                        {selectedProduct.originalPrice && parseFloat(selectedProduct.originalPrice) !== parseFloat(selectedProduct.currentPrice) && (
                          <>
                            <ModalOriginalPrice>₹{parseFloat(selectedProduct.originalPrice).toLocaleString('en-IN')}</ModalOriginalPrice>
                            <SavingsText>
                              You save ₹{(parseFloat(selectedProduct.originalPrice) - parseFloat(selectedProduct.currentPrice)).toLocaleString('en-IN')}
                            </SavingsText>
                          </>
                        )}
                      </>
                    )}
                  </ModalPriceContainer>

                  {variants && variants.length > 1 && (
                    <VariantSelector>
                      {variants.map((v) => (
                        <VariantChip
                          key={v.id}
                          $selected={v.id === selectedProduct.id}
                          onClick={() => setSelectedProduct(v)}
                        >
                          {v.name}
                        </VariantChip>
                      ))}
                    </VariantSelector>
                  )}

                  <ProductDetailsTable>
                    <tbody>
                      {selectedProduct.brand && (
                        <DetailRow>
                          <DetailLabel>Brand:</DetailLabel>
                          <DetailValue>{selectedProduct.brand}</DetailValue>
                        </DetailRow>
                      )}
                      {selectedProduct.category && (
                        <DetailRow>
                          <DetailLabel>Category:</DetailLabel>
                          <DetailValue>{selectedProduct.category}</DetailValue>
                        </DetailRow>
                      )}
                      {selectedProduct.sku && (
                        <DetailRow>
                          <DetailLabel>SKU:</DetailLabel>
                          <DetailValue>{selectedProduct.sku}</DetailValue>
                        </DetailRow>
                      )}
                    </tbody>
                  </ProductDetailsTable>

                  {(() => {
                    const prebookDesc = selectedProduct.descriptions?.find(desc => /pre-?book/i.test(desc.title || ''));
                    const prebookUrl = prebookDesc ? (prebookDesc.content.match(/(https?:\/\/[^\s]+)/) || [])[0] : null;
                    const displayDescriptions = selectedProduct.descriptions?.filter(desc => !/pre-?book/i.test(desc.title || ''));
                    
                    return displayDescriptions && displayDescriptions.length > 0 && (
                      <>
                        <DescriptionTitle>Key Features</DescriptionTitle>
                        <ProductDetailsTable>
                          <tbody>
                            {displayDescriptions.map((desc, index) => (
                              <DetailRow key={index}>
                                <DetailLabel>{desc.title || 'Feature'}</DetailLabel>
                                <DetailValue>{renderWithLinks(desc.content)}</DetailValue>
                              </DetailRow>
                            ))}
                          </tbody>
                        </ProductDetailsTable>
                      </>
                    );
                  })()}

                  {/* <DealProgress>
                    <DealProgressLabel>Deal Progress</DealProgressLabel>
                    <ModalProgressBar>
                      <ModalProgress width={`${selectedProduct.progress}%`} />
                    </ModalProgressBar>
                    <DealProgressText>
                      {selectedProduct.progress}% complete • {selectedProduct.daysRemaining} days remaining
                    </DealProgressText>
                  </DealProgress> */}

                  <ModalActions>
                    {(() => {
                      const start = selectedProduct.saleStartAt ? new Date(selectedProduct.saleStartAt).getTime() : null;
                      const end = selectedProduct.saleEndAt ? new Date(selectedProduct.saleEndAt).getTime() : null;
                      const isBefore = start && nowTs < start;
                      const isAfter = end && nowTs > end;
                      const isActive = (!start || nowTs >= start) && (!end || nowTs <= end);
                      const msTo = (target) => Math.max(0, target - nowTs);
                      const fmtDhms = (ms) => {
                        const totalSec = Math.floor(ms / 1000);
                        const days = Math.floor(totalSec / 86400);
                        const hours = Math.floor((totalSec % 86400) / 3600);
                        const mins = Math.floor((totalSec % 3600) / 60);
                        const secs = totalSec % 60;
                        return { days, hours, mins, secs };
                      };
                      const preMsg = isBefore && start ? (() => {
                        const t = fmtDhms(msTo(start));
                        return `Sale starts in ${t.days}d ${t.hours}h ${t.mins}m`;
                      })() : null;
                      const activeMsg = isActive && end ? (() => {
                        const t = fmtDhms(msTo(end));
                        return `Sale ends in ${t.days}d ${t.hours}h ${t.mins}m ${t.secs}s`;
                      })() : null;
                      return (
                        <>
                          {preMsg && (
                            <div style={{ color: '#dc3545', fontSize: 12 }}>{preMsg}</div>
                          )}
                          {activeMsg && (
                            <div style={{ color: '#16a34a', fontSize: 12 }}>{activeMsg}</div>
                          )}
                          {!isSubscriptionAllowed && (
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px', textAlign: 'center', fontWeight: '500' }}>
                              Subscribe to reveal the exclusive member-only deal price
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '10px', width: '100%', justifyContent: 'center' }}>
                            <BuyNowButton 
                              style={{ width: 'auto', padding: '12px 32px' }}
                              onClick={() => {
                                if (!isSubscriptionAllowed) {
                                  closeModal();
                                  onSubscribe();
                                } else {
                                  handleBuyNow(selectedProduct);
                                }
                              }}
                              disabled={isSubscriptionAllowed && (selectedProduct.stock === 0 || !isActive)}
                            >
                              {!isSubscriptionAllowed ? 'Subscribe' : (selectedProduct.stock === 0 ? 'Out of Stock' : (isAfter ? 'Sale Ended' : (isBefore ? 'Buy Now' : 'Buy Now')))}
                            </BuyNowButton>
                            
                            {(() => {
                              const prebookDesc = selectedProduct.descriptions?.find(desc => /pre-?book/i.test(desc.title || ''));
                              const prebookUrl = prebookDesc ? (prebookDesc.content.match(/(https?:\/\/[^\s]+)/) || [])[0] : null;

                              if (prebookUrl && isSubscriptionAllowed && isBefore) {
                                return (
                                  <a 
                                    href={prebookUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    style={{
                                      backgroundColor: '#e9fe53',
                                      color: '#202023',
                                      padding: '12px 32px',
                                      textDecoration: 'none',
                                      fontWeight: '800',
                                      border: '1px solid #202023',
                                      fontFamily: 'Metrophobic, sans-serif',
                                      fontSize: '16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      textAlign: 'center',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Pre-book now
                                  </a>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </>
                      );
                    })()}
                  </ModalActions>
                </ModalInfoSection>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Delivery Details Modal */}
        {deliveryModalOpen && selectedProduct && (
          <ModalOverlay onClick={closeDeliveryModal}>
            <DeliveryModalContent onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={closeDeliveryModal}>×</CloseButton>
              
              <DeliveryModalHeader>
                <DeliveryTitle>Confirm Your Order</DeliveryTitle>
                <ProductSummary>
                  <SummaryImage src={selectedProduct.image} alt={selectedProduct.name} />
                  <SummaryInfo>
                    <SummaryProductName>{selectedProduct.name}</SummaryProductName>
                    <SummaryPrice>₹{parseFloat(selectedProduct.currentPrice).toLocaleString('en-IN')}</SummaryPrice>
                  </SummaryInfo>
                </ProductSummary>
              </DeliveryModalHeader>

              <DeliveryForm>
                <SectionTitle>Delivery Details</SectionTitle>
                
                {loadingProfile && (
                  <LoadingProfileMessage>
                    Loading your profile information...
                  </LoadingProfileMessage>
                )}
                
                {profileLoaded && !loadingProfile && (
                  <ProfileLoadedMessage>
                    ✓ Your profile information has been loaded
                  </ProfileLoadedMessage>
                )}
                
                <FormRow>
                  <FormGroup>
                    <Label>Full Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      value={deliveryDetails.name}
                      onChange={handleDeliveryDetailsChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone Number *</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={deliveryDetails.phone}
                      onChange={handleDeliveryDetailsChange}
                      placeholder="Enter phone number"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Complete Address *</Label>
                  <TextArea
                    name="address"
                    value={deliveryDetails.address}
                    onChange={handleDeliveryDetailsChange}
                    placeholder="Enter your complete address (House/Flat No, Street, Landmark)"
                    required
                    rows="3"
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>Pincode *</Label>
                    <Input
                      type="text"
                      name="pincode"
                      value={deliveryDetails.pincode}
                      onChange={handleDeliveryDetailsChange}
                      placeholder="Enter pincode"
                      required
                      maxLength="6"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>City *</Label>
                    <Input
                      type="text"
                      name="city"
                      value={deliveryDetails.city}
                      onChange={handleDeliveryDetailsChange}
                      placeholder="Enter city"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>State *</Label>
                  <Select
                    name="state"
                    value={deliveryDetails.state}
                    onChange={handleDeliveryDetailsChange}
                    required
                  >
                    <option value="">Select State</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Delhi">Delhi</option>
                  </Select>
                </FormGroup>

                {/* Company Billing Section - only show if user has GST */}
                {deliveryDetails.gst && (
                  <BillingSection>
                    <CheckboxContainer>
                      <Checkbox
                        type="checkbox"
                        id="billToCompany"
                        checked={billToCompany}
                        onChange={(e) => setBillToCompany(e.target.checked)}
                      />
                      <CheckboxLabel htmlFor="billToCompany">
                        Bill to Company
                      </CheckboxLabel>
                    </CheckboxContainer>
                    
                    {billToCompany && (
                      <BillingFields>
                        <BillingFormGroup>
                          <Label>Company Name *</Label>
                          <Input
                            type="text"
                            name="companyName"
                            value={deliveryDetails.companyName}
                            onChange={handleDeliveryDetailsChange}
                            placeholder="Enter company name"
                            required
                          />
                        </BillingFormGroup>
                        <BillingFormGroup>
                          <Label>GSTIN *</Label>
                          <Input
                            type="text"
                            name="gst"
                            value={deliveryDetails.gst}
                            onChange={handleDeliveryDetailsChange}
                            placeholder="Enter GSTIN"
                            required
                          />
                        </BillingFormGroup>
                      </BillingFields>
                    )}
                  </BillingSection>
                )}

                <OrderSummary>
                  <SummaryRow>
                    <SummaryLabel>Product Price:</SummaryLabel>
                    <SummaryValue>₹{parseFloat(selectedProduct.currentPrice).toLocaleString('en-IN')}</SummaryValue>
                  </SummaryRow>
                  {serviceability.status === false && (
                    <SummaryRow>
                      <SummaryLabel>Serviceability:</SummaryLabel>
                      <SummaryValue style={{ color: '#dc3545', fontWeight: 700 }}>Not serviceable currently</SummaryValue>
                    </SummaryRow>
                  )}
                  <SummaryRow>
                    <SummaryLabel>Delivery Charges:</SummaryLabel>
                    <SummaryValue>Free</SummaryValue>
                  </SummaryRow>
                  <SummaryDivider />
                  <SummaryRow>
                    <TotalLabel>Total Amount:</TotalLabel>
                    <TotalValue>₹{parseFloat(selectedProduct.currentPrice).toLocaleString('en-IN')}</TotalValue>
                  </SummaryRow>
                </OrderSummary>

                <DeliveryActions>
                  <CancelButton onClick={closeDeliveryModal}>Cancel</CancelButton>
                  <ConfirmButton onClick={handleConfirmOrder} disabled={serviceability.status === false}>Confirm Order</ConfirmButton>
                </DeliveryActions>
              </DeliveryForm>
            </DeliveryModalContent>
          </ModalOverlay>
        )}
      </>
    );
  }
  const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px; /* Space above the button */
`;
const DealsSection = styled.section`
background-color: transparent;
align-self: stretch;
display: flex;
align-items: center;
color: #202023;
justify-content: center;
@media (max-width: 991px) {
  max-width: 100%;
  padding: 0 20px;
}
`;

const LimitedMerchandise = styled.p`
text-align: center;
letter-spacing: -0.42px;
margin-top: 42px;
font: 400 16px/130% Metrophobic, sans-serif;
@media (max-width: 991px) {
  max-width: 100%;
  margin-top: 40px;
  font-size: 14px;
  text-align:center;
}
`;
const DealsContent = styled.div`
display: flex;
width: 100%;
max-width: 1250px;
flex-direction: column;
align-items: center;
@media (max-width: 991px) {
  max-width: 100%;
}
`;

const CarouselWrapper = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 40px;
  margin-top: 30px;
  @media (max-width: 991px) {
    width: calc(100% + 40px);
    margin-left: -20px;
    margin-right: -20px;
  }
`;

const FeaturedBanner = styled.div`
  background-color: #000;
  display: flex;
  width: 100%;
  margin-top: 30px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  @media (max-width: 991px) {
    flex-direction: column-reverse;
    width: 100%; /* Reset to 100% as parent handles edge-to-edge */
    margin-top: 20px;
  }
`;

const FeaturedInfo = styled.div`
  flex: 1.2;
  padding: 35px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 991px) {
    padding: 20px;
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(233, 254, 83, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(233, 254, 83, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(233, 254, 83, 0); }
`;

const Badge = styled.div`
  background-color: #e9fe53;
  color: #000;
  padding: 6px 14px;
  font-weight: 900;
  display: inline-block;
  width: fit-content;
  margin-bottom: 25px;
  font-size: 13px;
  letter-spacing: 0.5px;
  animation: ${pulse} 2s infinite;
  box-shadow: 0 0 10px rgba(233, 254, 83, 0.3);
`;

const FeaturedTitle = styled.h1`
  font-size: 40px;
  font-weight: 900;
  text-transform: uppercase;
  color: #fff;
  margin: 0 0 18px 0;
  line-height: 0.9;
  letter-spacing: -1px;
  font-family: Metrophobic, sans-serif;
  @media (max-width: 991px) {
    font-size: 28px;
  }
`;

const SavingsBannerText = styled.div`
  font-size: 32px;
  font-weight: 900;
  color: #e9fe53;
  margin-bottom: 25px;
  font-family: Metrophobic, sans-serif;
  @media (max-width: 991px) {
    font-size: 24px;
  }
`;

const FeaturedPriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 20px;
  margin-bottom: 25px;
`;

const FeaturedPrice = styled.div`
  font-size: 34px;
  font-weight: 900;
  color: #e9fe53;
  font-family: Metrophobic, sans-serif;
  @media (max-width: 991px) {
    font-size: 26px;
  }
`;

const FeaturedOriginalPrice = styled.div`
  font-size: 17px;
  color: #666;
  text-decoration: line-through;
  font-weight: 400;
`;

const TimerContainer = styled.div`
  margin-bottom: 25px;
`;

const TimerLabel = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: #fff;
  text-transform: uppercase;
  margin-bottom: 15px;
  letter-spacing: 1px;
`;

const TimerBoxes = styled.div`
  display: flex;
  gap: 12px;
`;

const TimerBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimerDigit = styled.div`
  background-color: #fff;
  color: #000;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 900;
  font-family: Metrophobic, sans-serif;
`;

const TimerUnit = styled.div`
  font-size: 11px;
  margin-top: 8px;
  color: #999;
  font-weight: 600;
`;

const BannerActionGroup = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  margin-top: 10px;
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BuyNowBannerButton = styled.button`
  background-color: #e9fe53;
  color: #000;
  border: 2px solid #e9fe53;
  padding: 11px 28px;
  font-size: 15px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: Metrophobic, sans-serif;
  &:hover {
    background-color: #d4e647;
    border-color: #d4e647;
    transform: translateY(-2px);
  }
`;

const SecondaryBannerButton = styled.button`
  background-color: transparent;
  color: #fff;
  border: 2px solid #fff;
  padding: 11px 28px;
  font-size: 15px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: Metrophobic, sans-serif;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const FeaturedImageContainer = styled.div`
  flex: 0.8;
  background-color: #202023;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  @media (max-width: 991px) {
    height: 300px;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 25px 0 15px;
  margin-bottom: 10px;
  margin-top: 20px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const ProductCount = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #202023;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 15px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ToolbarSelect = styled.select`
  padding: 10px 15px;
  border: 1px solid #202023;
  background-color: transparent;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  cursor: pointer;
  border-radius: 0;
  min-width: 140px;
  @media (max-width: 768px) {
    flex: 1;
  }
`;
const ProductGrid = styled.div`
display: grid;
grid-template-columns: repeat(5, 1fr);
gap: 15px;
margin-top: 30px;
padding: 0;
width: 100%;

@media (max-width: 1200px) {
  grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 991px) {
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 768px) {
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (max-width: 480px) {
  grid-template-columns: 1fr;
}
`;

const ProductCard = styled.div`
border: 1px solid #000;
border-radius: 0;
padding: 15px 8px;
position: relative;
background-color: #fff;
cursor: pointer;
transition: all 0.3s ease;
box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
display: flex;
flex-direction: column;

&:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: #e9fe53;
}

@media (max-width: 768px) {
  padding: 12px;
}
`;
const ProductImageContainer = styled.div`
position: relative;
overflow: hidden;
border-radius: 8px;
height: 160px;
@media (max-width: 768px) {
  height: 140px;
}
`;

const ProductImage = styled.img`
width: 100%;
height: 100%;
object-fit: contain;
transition: transform 0.3s ease;

${ProductCard}:hover & {
  transform: scale(1.05);
}
`;

const ImageOverlay = styled.div`
position: absolute;
top: 0;
left: 0;
width: 100%;
height: 100%;
background-color: rgba(0, 0, 0, 0.6);
display: flex;
align-items: center;
justify-content: center;
border-radius: 8px;
opacity: 0;
transition: opacity 0.3s ease;

${ProductCard}:hover & {
  opacity: 1;
}
`;

const ViewDetailsText = styled.p`
color: white;
font-size: 16px;
font-weight: bold;
`;

const ProductInfo = styled.div`
margin-top: 10px;
display: flex;
flex-direction: column;
flex: 1;
`;

const ProgressBar = styled.div`
background-color: #e0e0e0;
height: 8px;
border-radius: 4px;
margin-top: 10px;
border: 1px solid #e0e0e0;
`;

const Progress = styled.div`
background-color: #e9fe53;
height: 100%;
border-radius: 4px;
width: ${props => props.width};
`;

const ProgressText = styled.p`
font-size: 12px;
color: #666;
margin: 5px 0;
`;

const RemainingText = styled.p`
font-size: 12px;
color: #666;
margin: 0;
`;

const ProductTitle = styled.h3`
font-size: 16px;
margin: 10px 0;
font-weight: 500;
`;

const RatingContainer = styled.div`
display: flex;
align-items: center;
margin: 5px 0;
`;

const RatingStars = styled.div`
color: #ffc107;
font-size: 14px;
`;

const RatingText = styled.span`
font-size: 12px;
color: #666;
margin-left: 5px;
`;

const PriceContainer = styled.div`
display: flex;
align-items: center;
gap: 10px;
margin-top: 5px;
`;

const CurrentPrice = styled.span`
  font-size: 14px;
  font-weight: 500;
  ${props => props.$isMasked && `
    user-select: none;
    letter-spacing: 1px;
  `}
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  text-decoration: line-through;
  color: #666;
  font-weight: 300;
`;

const SavingsBadge = styled.span`
position: absolute;
top: 0px;
right: 0px;
background-color: #e9fe53;
color: #202023;
padding: 5px 10px;
border-radius: 0;
font-size: 12px;
font-weight: bold;
border-left: 1px solid #202023;
border-bottom: 1px solid #202023;
`;

const CardFooter = styled.div`
margin-top: 10px;
`;

const BrandText = styled.p`
font-size: 12px;
color: #666;
margin-top: 5px;
`;

const StockText = styled.p`
font-size: 12px;
color: #dc3545;
font-weight: bold;
margin-top: 5px;
`;

const LoadingText = styled.p`
font-size: 16px;
color: #666;
margin-top: 20px;
`;

const ErrorText = styled.p`
font-size: 16px;
color: #ff0000;
margin-top: 20px;
`;

const RetryButton = styled.button`
margin-top: 10px;
padding: 8px 15px;
background-color: #202023;
color: #e9fe53;
border: 1px solid #202023;
border-radius: 4px;
cursor: pointer;
font-family: Metrophobic, sans-serif;
text-transform: uppercase;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 800px;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  
  @media (max-width: 768px) {
    width: 95%;
    max-height: 95%;
    border-radius: 8px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: #e0e0e0;
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  color: #333;
  z-index: 10;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const ModalImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const MainImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
`;

const ImageGallery = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  overflow-x: auto;
  padding-bottom: 10px;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  -ms-overflow-style: none;  /* Hide scrollbar for IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }
`;

const ThumbnailImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #e9fe53;
  }
`;

const ModalInfoSection = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #202023;
`;

const ModalRatingContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const ModalPriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 15px;
`;

const ModalCurrentPrice = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: #202023;
  ${props => props.$isMasked && `
    user-select: none;
    letter-spacing: 2px;
  `}
`;

const ModalOriginalPrice = styled.span`
  font-size: 18px;
  text-decoration: line-through;
  color: #666;
  margin-left: 10px;
`;

const SavingsText = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #ff0000;
  margin-left: 10px;
`;

const ProductDetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  border: 1px solid #d1d1d1;
`;

const DetailRow = styled.tr`
  border-bottom: 1px solid #d1d1d1;
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.td`
  padding: 12px;
  font-size: 13px;
  color: #666;
  font-weight: 500;
  border-right: 1px solid #d1d1d1;
  background-color: #f7f7f7;
  width: 35%;
`;

const DetailValue = styled.td`
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #202023;
  width: 65%;
`;

const DescriptionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #202023;
  margin-top: 10px;
`;

const DealProgress = styled.div`
  margin-bottom: 15px;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
`;

const DealProgressLabel = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #202023;
`;

const ModalProgressBar = styled.div`
  background-color: #e0e0e0;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
`;

const ModalProgress = styled.div`
  background-color: #e9fe53;
  height: 100%;
  border-radius: 5px;
  width: ${props => props.width};
`;

const DealProgressText = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 5px;
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  justify-content: center;
  align-items: center;
`;

const BuyNowButton = styled.button`
  padding: 12px 20px;
  background-color: #e9fe53;
  color: #202023;
  border: 0.5px solid #202023;
  border-width: 1px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  transition: background-color 0.3s ease;
  align-self: center;

  &:hover {
    background-color: #333;
    color: #e9fe53;
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const ViewDetailsButton = styled.button`
  display: block;
  margin: auto auto 0;
  padding: 8px 12px;
  background-color: #e9fe53;
  color: #202023;
  border: 1px solid #202023;
  border-radius: 2px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  transition: background-color 0.3s ease;
`;
const AddToListButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  background-color: #e9fe53;
  color: #202023;
  border: 1px solid #202023;
  border-radius: 8px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  transition: background-color 0.3s ease;

     &:hover {
     background-color: #d4e647;
   }
 `;

// Delivery Modal Styles
const DeliveryModalContent = styled.div`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 650px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  @media (max-width: 768px) {
    width: 95%;
    max-width: 100%;
    max-height: 95vh;
    border-radius: 8px;
  }
`;

const DeliveryModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
  border-radius: 12px 12px 0 0;
`;

const DeliveryTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 15px;
  color: #202023;
  text-align: center;
`;

const ProductSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const SummaryImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
`;

const SummaryInfo = styled.div`
  flex: 1;
`;

const SummaryProductName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #202023;
  line-height: 1.3;
`;

const SummaryPrice = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #202023;
  margin: 0;
`;

const DeliveryForm = styled.div`
  padding: 20px;
  overflow: hidden;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #202023;
  border-bottom: 2px solid #e9fe53;
  padding-bottom: 5px;
`;

const LoadingProfileMessage = styled.div`
  text-align: center;
  padding: 15px;
  background-color: #f0f8ff;
  border: 1px solid #e9fe53;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #666;
  font-style: italic;
`;

const ProfileLoadedMessage = styled.div`
  text-align: center;
  padding: 12px;
  background-color: #e8f5e8;
  border: 1px solid #28a745;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #155724;
  font-weight: 500;
`;

const FormRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FormGroup = styled.div`
  flex: 1;
  min-width: 0;
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  font-family: inherit;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #e9fe53;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #e9fe53;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  transition: border-color 0.3s ease;
  font-family: inherit;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #e9fe53;
  }
`;

const BillingSection = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  background-color: #fafafa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #202023;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: #333;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
`;

const BillingFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BillingFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const PaymentOptions = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const PaymentOption = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #e9fe53;
    background-color: #f8f9fa;
  }
`;

const RadioInput = styled.input`
  margin-right: 10px;
  transform: scale(1.2);
`;

const PaymentLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #333;
`;

const OrderSummary = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SummaryLabel = styled.span`
  font-size: 14px;
  color: #666;
`;

const SummaryValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #202023;
`;

const SummaryDivider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 15px 0;
`;

const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #202023;
`;

const TotalValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #202023;
`;

const DeliveryActions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  background-color: #fff;
  color: #202023;
  border: 0.5px solid #e0e0e0;
  border-width: 1px;
  border-radius: 5px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  transition: all 0.3s ease;

  &:hover {
    background-color: #f8f9fa;
    border-color: #ccc;
  }
`;

const ConfirmButton = styled.button`
  flex: 2;
  padding: 12px 20px;
  background-color: #e9fe53;
  color: #202023;
  border: 0.5px solid #202023;
  border-width: 1px;
  border-radius: 5px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  transition: all 0.3s ease;

  &:hover {
    background-color: #333;
    border-color: #333;
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    border-color: #ccc;
    cursor: not-allowed;
  }
`;

const PhilosophySection = styled.section`
  background-color: transparent;
  color: #000;
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1250px;
  margin-top: 30px;
  margin-bottom: 30px;
  box-sizing: border-box;
`;

const PhilosophyContent = styled.div`
  max-width: 1200px;
  width: 100%;
  text-align: center;
`;

const PhilosophyBadge = styled.div`
  color: #202023;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 15px;
  text-transform: uppercase;
`;

const PhilosophyTitle = styled.h2`
  font-size: 20px;
  line-height: 1.4;
  font-weight: 400;
  margin-bottom: 35px;
  font-family: Metrophobic, sans-serif;
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 25px;
  }
`;

const PhilosophyStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  margin-bottom: 35px;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    margin-bottom: 25px;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatNumber = styled.div`
  font-size: 50px;
  font-weight: 900;
  color: #000;
  line-height: 1;
  margin-bottom: 10px;
  font-family: Inter, sans-serif;
  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #000;
`;

const StatPlus = styled.div`
  font-size: 40px;
  color: #000;
  font-weight: 300;
  @media (max-width: 768px) {
    display: none;
  }
`;

const PhilosophyFooter = styled.div`
  border-top: 1px solid #000;
  padding-top: 25px;
  margin-top: 25px;
  font-size: 14px;
  line-height: 1.6;
  color: #000;
  font-family: Metrophobic, sans-serif;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const PhilosophyLink = styled.span`
  color: #000;
  cursor: pointer;
  font-weight: 700;
  text-decoration: underline;
  &:hover {
    color: #333;
  }
`;
 
 export default Dealsboard; 